/**
 * SUMBER TUNGGAL himpunan role untuk gate navigasi (Layout.jsx) dan gate
 * route (App.js).
 *
 * Kenapa berkas ini ada. Sebelum pemusatan, tiap himpunan ditulis DUA KALI:
 * sekali sebagai konstanta bernama di App.js, sekali lagi sebagai larik
 * telanjang di dalam kondisi JSX Layout.jsx. Komentar di kedua berkas saling
 * menyebut yang lain sebagai "cermin", dan cermin yang dijaga tangan akan
 * berpisah tanpa suara: mengubah satu tanpa yang lain menghasilkan menu yang
 * menampilkan halaman yang lalu ditolak router, atau halaman terjangkau yang
 * menunya tidak ada. Dua-duanya cacat hak akses yang tidak menimbulkan galat
 * apa pun saat build.
 *
 * ATURAN PAKAI. Jangan menulis larik role telanjang di App.js atau di
 * Layout.jsx. Tambahkan konstanta di sini, lalu impor. Larik telanjang yang
 * muncul kembali di kedua berkas itu mengembalikan cacat yang sama.
 *
 * BUKAN lapis keamanan. Penjaga yang sebenarnya adalah role guard di router
 * backend, dan RLS sebagai lapis kedua. Menyembunyikan menu dan menolak route
 * di frontend hanya soal pengalaman pakai: tanpa itu, role yang tidak berhak
 * tetap sampai ke layar lalu layarnya pecah jadi 403.
 */

// Role yang melihat modul Purchasing.
// FINANCE masuk sejak #4b-1 (Q65): read-only Vendor/PR/PO/Penerimaan,
// penuh di layar Tagihan/Pembayaran/DP.
export const PURCHASING_ROLES = [
  "OWNER", "KEPALA_CABANG", "ADMIN_PURCHASING", "ADMIN_GUDANG",
  "ADMIN_PRODUKSI", "CS", "FINANCE",
];

// Uang keluar ke vendor — cakupannya LEBIH SEMPIT dari PURCHASING_ROLES.
// Cermin INVOICE_READ_ROLES / PAYMENT_READ_ROLES / DP_READ_ROLES di
// backend/routers/purchasing.py, yang ketiganya berisi 4 role yang sama.
// Fase 3 (Owner FINAL 2026-07-29, F9) MENCABUT akses CS / ADMIN_GUDANG /
// ADMIN_PRODUKSI: mereka tidak perlu melihat nilai tagihan dan status bayar
// ke vendor. Tanpa konstanta kedua ini, ketiganya lolos route lalu layarnya
// pecah jadi 403 — bukan kebocoran data (backend tetap menolak), tapi
// pengalaman yang salah dan menyesatkan.
export const VENDOR_FINANCE_ROLES = [
  "OWNER", "KEPALA_CABANG", "ADMIN_PURCHASING", "FINANCE",
];

// Gate seksi Inventori secara keseluruhan. Lebih longgar dari tiap layar di
// dalamnya: yang melihat seksinya belum tentu melihat semua menunya.
export const INVENTORY_SECTION_ROLES = [
  "OWNER", "KEPALA_CABANG", "ADMIN_PRODUKSI", "ADMIN_GUDANG", "CS", "ADMIN_PURCHASING",
];

// Master Bahan.
export const INVENTORY_MATERIALS_ROLES = [
  "OWNER", "KEPALA_CABANG", "ADMIN_GUDANG", "ADMIN_PRODUKSI", "ADMIN_PURCHASING",
];

// Mutasi Stok Bahan.
export const INVENTORY_LEDGER_ROLES = [
  "OWNER", "KEPALA_CABANG", "ADMIN_GUDANG", "ADMIN_PURCHASING",
];

// Adjustment stok.
//
// Isinya kebetulan SAMA PERSIS dengan INVENTORY_OPNAME_ROLES hari ini. Keduanya
// sengaja TIDAK digabung: adjustment dan stock opname adalah dua kewenangan
// berbeda yang saat ini kebetulan dipegang role yang sama. Menggabungkannya
// membuat perubahan salah satu mustahil dilakukan tanpa ikut mengubah yang lain.
export const INVENTORY_ADJUSTMENT_ROLES = [
  "OWNER", "KEPALA_CABANG", "ADMIN_GUDANG",
];

// Stock Opname. Lihat catatan di INVENTORY_ADJUSTMENT_ROLES soal kenapa dua
// konstanta berisi sama tidak digabung.
export const INVENTORY_OPNAME_ROLES = [
  "OWNER", "KEPALA_CABANG", "ADMIN_GUDANG",
];

// Material Issue.
export const INVENTORY_MATERIAL_ISSUE_ROLES = [
  "OWNER", "KEPALA_CABANG", "ADMIN_PRODUKSI", "ADMIN_GUDANG",
];

// Layar master Tarif Borongan (Jahit) — `/master/tarif-borongan`.
//
// ⚠ PENGECUALIAN SADAR terhadap K65. K65 menyebut layar ini dengan namanya
// secara eksplisit — "Layar master (stage_capacity, fabric_cutting, seluruh
// master sewing_*) tetap OWNER + KEPALA_CABANG" (lihat juga
// `backend/routers/fabric_cutting_settings.py:27-29`, yang mengutip kalimat
// yang sama). K68 + K72 (dikunci Owner 23 Agustus 2026, SESUDAH K65) memberi
// FINANCE hak BACA lintas region atas layar ini secara spesifik — bukan
// pelonggaran umum "sewing_*", satu layar bernama ini saja.
//
// Karena itu layar ini TIDAK bisa ikut splat `/master/*` di masterRoutes.js
// (yang mewarisi `requireOwnerOrKepala` seragam dari App.js) — ia rute statis
// tersendiri dengan `roles={SEWING_RATE_ROLES}`, pola yang sama dengan
// `VENDOR_FINANCE_ROLES` di atas.
export const SEWING_RATE_ROLES = [
  "OWNER", "KEPALA_CABANG", "FINANCE",
];

// Seksi Special Ops — FOC Requests dan Piutang.
//
// Diambil PERSIS dari pagar seksi Special Ops di Layout.jsx, yang berbunyi
// `(isOwner || isKepalaCabang || isCS)`. Ketiga penanda itu dipetakan di
// contexts/AuthContext.jsx ke kode peran OWNER, KEPALA_CABANG, dan CS.
//
// SENGAJA BUKAN `requireOwnerOrKepala` yang sudah ada di ProtectedRoute:
// himpunannya berbeda, karena pagar menu ikut memuat CS. Memakai
// `requireOwnerOrKepala` akan MENYEMPITKAN akses dari yang menunya tampilkan,
// dan itu perubahan kewenangan, bukan pemasangan pagar.
//
// Butir ketiga di seksi yang sama, Special Customers, TIDAK memakai konstanta
// ini: ia punya pagar sendiri `(isOwner || isKepalaCabang)` di Layout.jsx, dan
// route-nya sudah memakai `requireOwnerOrKepala` yang cocok dengan itu.
export const SPECIAL_OPS_ROLES = [
  "OWNER", "KEPALA_CABANG", "CS",
];

// =====================================================================
// DUA HIMPUNAN DI BAWAH INI MIRIP TAPI BUKAN HAL YANG SAMA
// =====================================================================
// Keduanya sengaja ditaruh berdampingan justru supaya bedanya terbaca.
// Menyamakannya adalah kekeliruan yang sudah benar-benar terjadi: sebuah
// brief unit kerja menuliskan bahwa layar Users "masih menuntut region+store
// untuk role lintas region", dan menyimpulkan daftar ini harus diisi
// OWNER + ADMIN_PURCHASING + FINANCE — tanpa DESIGNER_EDITOR. Waktu itu
// perintahnya ditolak dan itu BENAR: backend belum berubah, jadi layar akan
// mengirim baris yang justru ditolak endpoint-nya dengan 400 INVALID_SCOPE.
//
// ⚠ Sesudah K45 (18 Agustus 2026) separuh premis itu berubah jadi benar dan
// separuh lagi tetap salah, dan itulah kenapa catatan ini dipertahankan:
// ADMIN_PURCHASING + FINANCE memang HARUS masuk sekarang, tapi
// DESIGNER_EDITOR tetap TIDAK BOLEH keluar. Daftar yang benar ada EMPAT, dan
// brief lama itu menyebut TIGA.
//
//   WHITELIST_NO_SCOPE_ROLES  = siapa yang barisnya WAJIB region+store NULL
//                               saat DIBUAT lewat endpoint whitelist.
//   REGION_UNBOUND_ROLES      = siapa yang BOLEH MELIHAT lintas region
//                               saat aplikasi berjalan.
//
// Irisannya cuma TIGA dari empat. Perbedaannya bukan kelalaian, dan tetap ada
// sesudah FINANCE-03 ditutup: DESIGNER_EDITOR wajib NULL/NULL saat barisnya
// dibuat, tapi ia TIDAK membaca lintas region saat aplikasi berjalan
// (`roles.is_region_unbound` = FALSE untuk dia).
// =====================================================================

// Role yang barisnya di whitelist WAJIB ber-region_id dan store_id NULL.
//
// CERMIN `_validate_scope` di `backend/routers/users.py`. Diperbarui
// 19 Agustus 2026 (FINANCE-02) mengikuti perubahan backend K45 pada
// 18 Agustus (FINANCE-03 DITUTUP): cabang harfiah `role_code == "OWNER"`
// di sana sudah diganti keanggotaan `role_code in REGION_UNBOUND_ROLES`,
// sehingga ADMIN_PURCHASING dan FINANCE — dua role yang migration 013a
// tandai `is_region_unbound = TRUE` persis seperti OWNER — kini WAJIB
// NULL/NULL, bukan lagi jatuh ke cabang staff. Keputusan Owner: "NULL/NULL
// sejajar OWNER — role lintas region tidak wajib region_id, tidak wajib
// store_id."
//
// ⚠ LIMA, dan itu 3 + 2 — jangan diturunkan dari `REGION_UNBOUND_ROLES`.
// Himpunan itu berisi TIGA. `DESIGNER_EDITOR` dan `SPV_DESAINER` tidak ada di
// dalamnya: keduanya masuk daftar ini lewat cabang `_validate_scope` miliknya
// SENDIRI — jalurnya PENUGASAN, bukan flag (Q13 untuk desainer, K56 poin 3 dan
// poin 4 untuk SPV; seluruh tim desain di Samarinda melayani ketiga cabang).
// Dua sebab berbeda, satu akibat yang sama di layar. Menurunkan daftar ini dari
// `REGION_UNBOUND_ROLES` karena ia "terlihat seperti satu sumber kebenaran"
// akan menghapus keduanya tanpa satu test pun merah — layar lalu menuntut
// region+store, dan endpoint menolak 400 INVALID_SCOPE.
//
// ⚠ Sebaliknya juga terlarang: memindahkan keduanya KE `REGION_UNBOUND_ROLES`
// supaya daftarnya jadi satu. K56 poin 3 menahan `is_region_unbound` tetap
// FALSE untuk keduanya, karena flag itu ikut membuka PDF Archive dan
// purchasing — akses yang tidak satu pun keputusan berikan.
//
// Aturan urutan yang melahirkan komentar ini masih berlaku ke depan:
// himpunan ini CERMIN, dan cermin berubah SESUDAH yang dicerminkan berubah.
// Versi sebelumnya pernah ditolak diubah justru karena backend-nya belum
// berubah saat itu — penolakan itu benar pada waktunya, dan berhenti benar
// begitu K45 masuk. `SPV_DESAINER` ditambahkan 23 Agustus 2026 SESUDAH
// `_validate_scope` menerimanya, dalam unit kerja yang sama — urutan itu
// dituntut pesan gagal `test_d4_empat_role_itu_tepat_yang_lolos_validate_scope_null_null`.
// K236 (1 Nov 2026): + PEMANTAU — hanya baca; membaca seluruh cabang lewat tampilan baca backend, bukan flag.
export const WHITELIST_NO_SCOPE_ROLES = [
  "OWNER", "DESIGNER_EDITOR", "ADMIN_PURCHASING", "FINANCE", "SPV_DESAINER", "PEMANTAU",
];

// ⚠ Keterangan cakupan yang dipasang Users.jsx untuk anggota daftar di atas
// TIDAK tinggal di sini, dan itu disengaja. Ia satu ekspresi di titik
// pakainya (`Users.jsx`, kolom region baris draft), karena isinya hanya
// membedakan OWNER dari sisanya — bukan data per peran. Peta lima baris
// sempat ada di sini pada 25 Agustus 2026 dan dibongkar hari itu juga: empat
// dari lima nilainya kembar, dan tiap entri mengulang nama peran yang sudah
// dicetak judul kartu dari `role.name`. Alasan lengkapnya ada di Users.jsx.

// Role yang membaca lintas region saat aplikasi berjalan.
//
// Cermin `roles.is_region_unbound` (migration 013a) dan
// `current_user_is_region_unbound()` di RLS. Backend menyimpan salinan lokal
// dengan isi yang sama persis di `services/grn_service.py`,
// `services/attendance_service.py`, `services/fabric_cutting_service.py`, dan
// `dev_impersonation.py`.
//
// Dipindahkan ke sini dari `components/dev/DevRoleSwitcher.jsx` supaya ia
// tidak ikut lenyap saat berkas DEV ONLY itu dihapus sebelum Production —
// himpunan ini menjelaskan sifat role, bukan sifat perkakas uji.
export const REGION_UNBOUND_ROLES = [
  "OWNER", "ADMIN_PURCHASING", "FINANCE",
];

// Butir navigasi "Tugas Desainer" (Layout.jsx).
//
// CERMIN `DESIGN_TASK_READ_ROLES` di `backend/routers/design_tasks.py`, yang
// sendirinya RUJUKAN ke `DR_READ_ROLES` di `backend/routers/design_requests.py`
// — bukan salinan. Enam role.
//
// Urutannya benar dan itu dituntut aturan di kepala berkas ini: cermin berubah
// SESUDAH yang dicerminkan berubah. Penjaga backend-nya mendarat lebih dulu
// (K89 + K90, commit yang sama dengan unit ini); larik ini menyusul.
//
// KENAPA ADA. Sebelum 24 Agustus 2026 butir navigasi ini NOL digerbangi —
// satu-satunya di antara tetangganya yang tampil untuk setiap role bersesi.
// Terukur: ADMIN_GUDANG dan ADMIN_PURCHASING membuka menunya dan menerima 94
// task lengkap dengan kolom DR tersemat, sementara `/api/design-requests/{id}`
// menolak mereka 403 (K83).
//
// ⚠ MENYEMBUNYIKAN MENU BUKAN MENGGERBANGI ROUTE, dan jangan dibaca begitu.
// `App.js:205-206` memakai `openToAllRoles` dan SENGAJA tidak diubah: K71
// menyatakan mengubah daftar route terbuka adalah keputusan Owner tersendiri.
// Yang mengetik URL-nya langsung tetap sampai ke halaman; yang menjaganya 403
// backend. Itu model K71 apa adanya, bukan kekurangan yang tertinggal.
//
// ⚠ K89 MENOLAK BENTUK "menu ada tapi daftarnya selalu kosong". Itu sebabnya
// backend membalas 403 dan bukan daftar kosong, dan sebabnya larik ini ada:
// menu yang selalu berujung 403 sama menyesatkannya — orang membaca sistemnya
// rusak, bukan aksesnya memang tidak ada.
export const DESIGN_TASK_NAV_ROLES = [
  "OWNER", "KEPALA_CABANG", "CS", "ADMIN_PRODUKSI", "DESIGNER_EDITOR", "SPV_DESAINER",
];

// Butir navigasi "Piutang" + route `/receivables` (Layout.jsx, App.js).
//
// CERMIN `RECEIVABLE_READ_ROLES` di `backend/routers/receivables.py`. Empat role.
//
// ⛔ LARIK TERSENDIRI, DAN ITU MENGIKAT — JANGAN pakai `SPECIAL_OPS_ROLES`.
//    Larik itu dipakai BERTIGA: Special Customers, FOC Requests, dan Piutang.
//    Menambahkan FINANCE ke sana akan membuka DUA modul yang K34 **secara
//    eksplisit menolak** — `special_customers` dan `foc_requests` keduanya ada
//    di daftar "TIDAK DITAMBAHKAN (21 policy)" milik K34. Pelebaran dua modul
//    dalam satu baris, tanpa terlihat sebagai keputusan.
//
// KENAPA FINANCE ADA DI SINI. K34 memberi Finance hak baca piutang lintas
// region ("piutang wilayahnya paling inti"), terpasang di RLS sejak
// `20260814100329_k34_finance_select.sql` — tapi seluruh backend memakai
// service role yang mem-bypass RLS, jadi selama sepuluh hari hak itu benar di
// database dan tidak ada di router. Terukur 24 Agustus 2026: FINANCE menerima
// 200 dengan `count = 0` dari daftar dan 403 dari detail.
//
// ⚠ MELIHAT != MENGERJAKAN. K34 mengunci Finance READ-only ("SELECT saja, NOL
// jalur tulis"). Layar Piutang punya tombol pelunasan; yang menahannya
// `_scope_check` di backend, yang SENGAJA nol memuat FINANCE. Menyembunyikan
// tombolnya di layar bukan keamanan — tapi menampilkan tombol yang selalu 403
// juga bentuk yang K89 tolak. Kalau layarnya dibangun/ditinjau, itu yang perlu
// diputuskan; larik ini hanya soal PINTU MASUKNYA.
//
// Urutannya benar dan itu dituntut aturan di kepala berkas ini: cermin berubah
// SESUDAH yang dicerminkan berubah. Penjaga backend-nya mendarat di commit yang
// sama dengan larik ini.
export const RECEIVABLE_NAV_ROLES = [
  "OWNER", "KEPALA_CABANG", "CS", "FINANCE",
];

// Refund customer (Phase 1F). CERMIN dari REFUND_READ_ROLES di
// `backend/services/refund_service.py` — larik ini cuma menentukan siapa yang
// boleh MEMBUKA layarnya.
//
// ⚠ Yang menentukan siapa boleh MENYETUJUI dan siapa boleh MENGELUARKAN UANG
// BUKAN larik ini, melainkan `meta.permissions` yang dikirim backend per
// permintaan. CS ada di sini karena ia yang mendengar customernya dan boleh
// MENGAJUKAN — ia nol boleh mengeluarkan uang, dan itu ditegakkan backend.
export const REFUND_NAV_ROLES = [
  "OWNER", "KEPALA_CABANG", "FINANCE", "CS",
];

// Komplain customer (Phase 1F H1).
//
// ⛔ FINANCE SENGAJA NOL ADA DI SINI, dan itu cermin K34: Finance mendapat
// seluruh data UANG lintas region, tapi `customers` DITOLAK eksplisit.
// Komplain memuat keluhan customer, bukan angka. Menambahkan FINANCE "supaya
// seragam dengan refund" akan membuka data pribadi lintas cabang tanpa satu
// pun keputusan yang memberikannya.
export const COMPLAINT_NAV_ROLES = [
  "OWNER", "KEPALA_CABANG", "CS", "ADMIN_PRODUKSI",
];

// Papan kerja harian + registri personil per tahap (K50, K125-K127).
//
// CERMIN dari BACA_ROLES di `routers/papan_kerja.py` dan
// `routers/personil_tugas.py`. Larik ini cuma menentukan siapa boleh MEMBUKA
// layarnya — penyaringan region tetap milik backend, dan KC selalu melihat
// regionnya sendiri apa pun yang layar kirim.
//
// ⚠ ADMIN_PRODUKSI ikut, dan itu K126 butir 4: saat ketua dan anggota satu
// tahap sama-sama nol hadir, dialah yang menjalankan tugas hariannya.
export const PAPAN_KERJA_ROLES = [
  "OWNER", "KEPALA_CABANG", "ADMIN_PRODUKSI",
];

// Arsip PDF (Fase 6 / Phase 1G). CERMIN dari `PDF_READ_ROLES` di
// `backend/routers/pdf_archive.py` — larik ini cuma menentukan siapa yang
// boleh MEMBUKA layarnya; penyaringan region/store tetap milik backend.
//
// ⛔ DESIGNER_EDITOR dan SPV_DESAINER SENGAJA NOL ADA — blanket 403 di PDF
// Archive (CLAUDE.md, model peran). Menambahkan mereka "supaya semua bisa
// lihat dokumen" akan membuka tagihan vendor dan harga beli ke tim desain.
export const PDF_ARCHIVE_ROLES = [
  "OWNER", "KEPALA_CABANG", "ADMIN_PURCHASING", "ADMIN_GUDANG",
  "ADMIN_PRODUKSI", "CS", "FINANCE",
];

// Review desain oleh SPV (K55). SPV_DESAINER memberi vonis; OWNER ikut
// MEMBACA karena pengawasan butuh melihat antrean yang menumpuk.
//
// ⚠ Owner MEMBACA, bukan MEMVONIS. Pembedanya bukan daftar ini melainkan
// CHECK `reviewer_role_code = 'SPV_DESAINER'` di tabelnya — daftar ini cuma
// menentukan siapa yang boleh membuka layarnya.
export const SPV_REVIEW_ROLES = [
  "SPV_DESAINER", "OWNER",
];

// Jam masuk kerja per wilayah (K134, kelompok HRD).
//
// ⛔ CERMIN dari `JAM_MASUK_TULIS_ROLES` di `backend/routers/hr_attendance.py`.
// Larik ini cuma menentukan siapa yang melihat butir menunya; siapa yang boleh
// MENGUBAH tetap ditegakkan backend — menyembunyikan menu bukan keamanan.
//
// ⚠ ADMIN_PRODUKSI sengaja NOL ADA walau ia yang paling terdampak jadwalnya.
// Jam masuk adalah keputusan kepegawaian cabang; yang terkena akibatnya bukan
// yang berhak menetapkannya.
export const JAM_MASUK_NAV_ROLES = ["OWNER", "KEPALA_CABANG"];

// Usulan auto-draft WO (butir H, Q35). CERMIN dari `WO_SUBMIT_APPROVAL_ROLES`
// di `backend/routers/wo.py` — yang memeriksa usulan adalah yang kelak
// mengajukannya ke Kepala Cabang.
//
// ⚠ KEPALA_CABANG sengaja NOL ADA. Ia menyetujui di AKHIR (Q36); memberinya
// layar usulan berarti ia melihat daftar yang belum diperiksa Admin Produksi,
// dan pemeriksaan dua tingkat runtuh jadi satu.
export const WO_USUL_ROLES = ["OWNER", "ADMIN_PRODUKSI"];

// Dashboard percakapan customer. CERMIN dari `BACA_ROLES` di
// `backend/routers/percakapan.py`.
//
// ⛔ DESIGNER_EDITOR dan SPV_DESAINER SENGAJA NOL ADA — mereka mengerjakan
// desainnya, bukan hubungan dengan customernya, dan percakapan memuat nomor
// telepon serta pembicaraan harga. Blanket 403 yang sama dengan PDF Archive.
//
// ⚠ ADMIN_PURCHASING dan FINANCE juga nol ada walau keduanya lintas region:
// lintas region soal CAKUPAN, bukan soal berhak.
// ⛔ K206 / VONIS-HAK-BACA-ISI-FA: ADMIN_PRODUKSI dicabut — kontak customer hanya Owner/KC/CS.
export const PERCAKAPAN_ROLES = ["OWNER", "KEPALA_CABANG", "CS"];

// K207 W6: layar Kanal WhatsApp (keadaan kanal, pesan belum terkirim + kirim manual F1). CERMIN `KANAL_ROLES` di
// `backend/routers/wa_ketahanan.py` — antreannya memuat nomor customer, jadi peran sama dengan Percakapan (K206).
// Pindah nomor (F3) dan latihan: Owner saja (dijaga server).
export const KANAL_WA_ROLES = ["OWNER", "KEPALA_CABANG", "CS"];

// Serah terima antar tahap + rekap upah borongan.
// CERMIN dari `HANDOVER_WRITE_ROLES`/`HANDOVER_READ_ROLES` di
// `backend/services/stage_handover_service.py`.
//
// ⚠ FINANCE ikut MEMBACA (K30) tapi nol MENCATAT — dua larik terpisah, bukan
// satu yang dilonggarkan. Melonggarkan yang tulis akan menampilkan tombol yang
// backend-nya tetap menolak.
export const BORONGAN_TULIS_ROLES = ["OWNER", "KEPALA_CABANG", "ADMIN_PRODUKSI"];
export const BORONGAN_BACA_ROLES = ["OWNER", "KEPALA_CABANG", "ADMIN_PRODUKSI", "FINANCE"];

// Rekap absensi bulanan. CERMIN dari `ATTENDANCE_READ_ROLES` +
// `REGION_UNBOUND_ROLES` di `backend/services/attendance_service.py`.
export const ABSENSI_REKAP_ROLES = [
  "OWNER", "KEPALA_CABANG", "ADMIN_PRODUKSI", "ADMIN_GUDANG", "CS",
  "ADMIN_PURCHASING", "FINANCE",
];

// Tracking pesanan (K137). CERMIN dari `BACA_ROLES` di
// `backend/routers/tracking.py`.
//
// ⚠ CS ADA DI DEPAN — ia yang mengangkat telepon, dan modul ini lahir untuk
// pertanyaannya. ⛔ DESIGNER_EDITOR dan SPV_DESAINER nol ada: jejak memuat
// nominal pembayaran, dan nol satu keputusan pun memberi tim desain akses ke
// angka uang (pola yang sama dengan PDF Archive).
export const TRACKING_ROLES = ["OWNER", "KEPALA_CABANG", "CS", "ADMIN_PRODUKSI", "FINANCE"];

// Laporan P5 (31 Agu 2026). CERMIN daftar-putih backend `routers/laporan.py`
// — menu ini gabungan (union) peran keempat endpointnya; tiap endpoint tetap
// menolak sendiri-sendiri di backend (RLS/guard otoritatif, menu cuma pintu).
// ⛔ ADMIN_GUDANG ada di sini KARENA valuasi persediaan — bukan penjualan;
//    backend menolaknya di endpoint lain, dan itu yang benar.
export const LAPORAN_ROLES = ["OWNER", "KEPALA_CABANG", "FINANCE", "ADMIN_GUDANG", "CS"];

// Papan Kontrol (K180). CERMIN `PERAN_BACA` di routers/papan_kontrol.py —
// penyimpangan prosedur & data; tutup dengan alasan hanya Owner/KC (Q29),
// ambang hanya Owner (Q32) dan KC membaca.
export const PAPAN_KONTROL_ROLES = ["OWNER", "KEPALA_CABANG", "ADMIN_PRODUKSI", "ADMIN_GUDANG", "FINANCE"];
export const PAPAN_KONTROL_AMBANG_ROLES = ["OWNER", "KEPALA_CABANG"];

// Papan TV ruang kerja (K137). Sejak K190 butir 7 (BUSINESS-13, 22 Sep 2026)
// layar TV = SATU-SATUNYA route `openToAllRoles`; larik ini tinggal untuk
// butir NAVIGASI (siapa yang melihat menunya di sidebar) — menu tidak perlu
// muncul untuk semua orang, tetapi URL-nya boleh dibuka siapa pun yang login.
export const PAPAN_TV_ROLES = ["OWNER", "KEPALA_CABANG", "ADMIN_PRODUKSI"];

// ---------------------------------------------------------------------------
// K190 butir 7 (22 Sep 2026): `openToAllRoles` HANYA untuk Papan TV. Route
// lain yang dulu memakainya kini menyebut perannya — CERMIN guard backend,
// tidak ditambah dan tidak dikurangi (K65: route tidak boleh lebih longgar
// dari backend; backend tetap penjaga sungguhan).
// ---------------------------------------------------------------------------
// Sembilan peran resmi (CLAUDE.md §Model peran). Dipakai untuk cangkang
// <Layout/> dan Dashboard (butir per peran, `/dashboard/butir`). Daftar FA sejak K206 memakai FA_LIST_ROLES.
export const SEMUA_PERAN = [
  "OWNER", "KEPALA_CABANG", "CS", "ADMIN_PRODUKSI", "ADMIN_GUDANG",
  "ADMIN_PURCHASING", "FINANCE", "DESIGNER_EDITOR", "SPV_DESAINER",
];
// Cermin `CUSTOMER_ACCESS_ALLOWED_ROLES` routers/customers.py. K206 butir 6 (12 Okt 2026): nama & kontak
// customer hanya Owner/KC/CS — Admin Produksi/Gudang/Purchasing dicabut (cermin RLS `customers_select_scoped`).
// Dijaga `test_k206_butir6_nama_per_jalur_murni.py::test_layar_customer_cermin_pintu`.
export const CUSTOMER_ROLES = ["OWNER", "KEPALA_CABANG", "CS"];
// Cermin `DR_READ_ROLES` routers/design_requests.py:116 (= DESIGN_TASK_READ_ROLES).
export const DR_READ_ROLES = [
  "OWNER", "KEPALA_CABANG", "CS", "ADMIN_PRODUKSI", "DESIGNER_EDITOR", "SPV_DESAINER",
];
// Cermin `DR_CREATE_ROLES` routers/design_requests.py:68.
export const DR_CREATE_ROLES = ["OWNER", "KEPALA_CABANG", "CS"];
// Cermin `_fa_scope_check` routers/fa.py:85 (detail) dan pembuat draft fa.py:312.
export const FA_DETAIL_ROLES = ["OWNER", "KEPALA_CABANG", "CS"];
// K206 / VONIS-HAK-BACA-ISI-FA (8 Okt 2026) — CERMIN `services/hak_baca_fa.py`.
// Isi FA lengkap (nama/kontak customer, barang, harga, catatan): Owner, KC, CS.
export const FA_ISI_ROLES = ["OWNER", "KEPALA_CABANG", "CS"];
// Daftar FA: tiga peran di atas (isi lengkap) + empat peran RINGKAS (nomor FA, status, tenggat — diproyeksikan
// backend). Finance (nominal lewat Piutang) dan Admin Purchasing TIDAK ada: backend menolak 403.
export const FA_RINGKAS_ROLES = ["ADMIN_PRODUKSI", "ADMIN_GUDANG", "DESIGNER_EDITOR", "SPV_DESAINER"];
export const FA_LIST_ROLES = [...FA_ISI_ROLES, ...FA_RINGKAS_ROLES];
export const FA_CREATE_ROLES = ["OWNER", "KEPALA_CABANG", "CS"];
// Cermin `WO_READ_ALLOWED_ROLES` / `WO_WRITE_ALLOWED_ROLES` routers/wo.py:54,60.
export const WO_READ_ROLES = ["OWNER", "KEPALA_CABANG", "ADMIN_PRODUKSI", "CS", "ADMIN_GUDANG"];
export const WO_WRITE_ROLES = ["OWNER", "KEPALA_CABANG", "ADMIN_PRODUKSI"];

// FA Lama / cutover (Q39–Q45, K163) — 14 Sep 2026. Lima peran yang service
// `fa_lama` sebut: Owner buka jendela · KC tutup + rekonsiliasi · CS isi ·
// Admin Produksi tahap terakhir · Finance verifikasi. Backend tetap pagar
// utamanya per rute (`_wajib_peran`); daftar ini hanya pintu layar.
export const FA_LAMA_ROLES = ["OWNER", "KEPALA_CABANG", "CS", "ADMIN_PRODUKSI", "FINANCE"];

// Aset & Maintenance (Tahap 4b, 16 Sep 2026). Cermin `aset_meta.MENU.jabatan`
// (L2): OWNER lintas region, tiga jabatan region. PIC jabatan lain nol menu —
// inputnya diwakilkan. FINANCE/ADMIN_PURCHASING ditolak 403 `_lingkup_baca`.
export const ASET_NAV_ROLES = ["OWNER", "KEPALA_CABANG", "ADMIN_GUDANG", "ADMIN_PRODUKSI"];

// Tombol tulis aset & jadwal (buat/ubah/status/nonaktif/aktif) — cermin
// `aset_service.PERAN_KELOLA` (Q179, K107 butir 1). ADMIN_GUDANG /
// ADMIN_PRODUKSI baca saja di Daftar/Detail Aset; hak catat mereka
// (servis, laporan, km) datang di layar 4b-2/4b-3. Hapus = OWNER saja (L9/R12).
export const ASET_KELOLA_ROLES = ["OWNER", "KEPALA_CABANG"];

// Serah terima barang jadi ke CUSTOMER (K166 butir 1) — CERMIN
// `backend/services/serah_terima_customer.PERAN_SERAH_TERIMA`; dijaga
// `test_serah_terima_customer_cermin_frontend.py` dua arah terhadap PENJAGA backend.
export const SERAH_TERIMA_CUSTOMER_ROLES = ["OWNER", "KEPALA_CABANG", "CS", "ADMIN_GUDANG"];
// Ubah metode pengiriman — vonis Owner 10 Sep 2026: PERSIS DUA (Owner sengaja tidak).
// CERMIN `PERAN_UBAH_METODE`.
// Tindak Lanjut Pesanan (BUSINESS-50/51/52, 26 Sep 2026) — CERMIN `services/tindak_lanjut_pesanan.PERAN_LIHAT`.
// Aksi per tombol (CS ajukan batal, KC putus batal, CS/KC ajukan izin, Owner putus izin) ditegakkan di backend.
export const TINDAK_LANJUT_PESANAN_ROLES = ["OWNER", "KEPALA_CABANG", "CS"];
export const SERAH_TERIMA_UBAH_METODE_ROLES = ["CS", "KEPALA_CABANG"];
// Label QR FA (S11) — CERMIN `routers/label_qr.LABEL_ROLES`.
export const LABEL_QR_ROLES = ["OWNER", "KEPALA_CABANG", "ADMIN_PRODUKSI"];

// Celah 3 (langkah 35, ISIAN §8): satu layar approval barang berkurang — penyetuju pengeluaran bahan pengganti
// (MI_APPROVE_ROLES backend: OWNER, KEPALA_CABANG, ADMIN_GUDANG); penyesuaian hilang/rusak disetujui OWNER/KC saja.
export const BARANG_BERKURANG_ROLES = ["OWNER", "KEPALA_CABANG", "ADMIN_GUDANG"];
// K197 butir 5: pindah bahan baku antar store — AG mengajukan/kirim/terima, KC menyetujui, Owner membaca.
export const PINDAH_BAHAN_ROLES = ["OWNER", "KEPALA_CABANG", "ADMIN_GUDANG"];
// Inventaris A13 — laporan balik Telegram dikonfirmasi peran tulis WO (cermin services/laporan_balik.PEMUTUS).
export const LAPORAN_BALIK_ROLES = ["OWNER", "KEPALA_CABANG", "ADMIN_PRODUKSI"];
// Inventaris A18 — data gaji rahasia: Owner + Finance (cermin services/payroll.BACA).
export const PAYROLL_ROLES = ["OWNER", "FINANCE"];
// K212 (BUSINESS-80/83) — layar Uang Masuk: settlement DOKU + uang kembali pemasok. Finance mencatat, Owner membaca
// (cermin `backend/routers/uang_masuk.py::PERAN_BACA`).
export const UANG_MASUK_ROLES = ["OWNER", "FINANCE"];
// K214 (BUSINESS-87) — daftar SISA uang kembali pemasok di layar Retur Vendor / Uang Muka Vendor: ditagih Admin
// Purchasing, dibaca juga Finance + Owner (cermin `backend/services/uang_kembali_vendor.py::PERAN_BACA_SISA`).
export const SISA_UANG_KEMBALI_ROLES = ["FINANCE", "OWNER", "ADMIN_PURCHASING"];
// K215 (BUSINESS-90) — panel UANG LEBIH dari pemasok di layar Retur Vendor / Uang Muka Vendor / Uang Masuk: Admin
// Purchasing memilih, KC menyetujui (cabangnya), Finance mentransfer balik, Owner membaca
// (cermin `backend/services/uang_lebih_pemasok.py::PERAN_BACA`).
export const UANG_LEBIH_ROLES = ["OWNER", "FINANCE", "ADMIN_PURCHASING", "KEPALA_CABANG"];

// Langkah 25 KPI ketepatan waktu (ISIAN §4 B3): Owner semua, KC cabangnya, staf dirinya sendiri.
// Peran yang punya dimensi KPI: pencatat tahap/serah terima (ADMIN_PRODUKSI), penangan komplain +
// penagih piutang (CS). Penyaring sebenarnya di backend `services/kpi.py::lingkup`.
export const KPI_ROLES = ["OWNER", "KEPALA_CABANG", "CS", "ADMIN_PRODUKSI"];

// Karyawan per cabang (ISIAN §9): CERMIN `EMPLOYEE_WRITE_ROLES` di backend/services/attendance_service.py.
export const KARYAWAN_ROLES = ["OWNER", "KEPALA_CABANG"];

// Alarm bahan (TUJUAN "YANG MELIHAT ALARM" + Admin Gudang baca saja, K203 butir 10): CERMIN `ALARM_BAHAN_ROLES` di backend/routers/inventory.py.
export const ALARM_BAHAN_ROLES = ["OWNER", "KEPALA_CABANG", "ADMIN_PRODUKSI", "ADMIN_GUDANG"];

// Analitik K179: gabungan peran keempat tab (penjaga per tab di backend routers/analitik.py).
export const ANALITIK_ROLES = ["OWNER", "KEPALA_CABANG", "ADMIN_PRODUKSI", "ADMIN_PURCHASING", "ADMIN_GUDANG", "CS"];

// Rekap Tanya ETAMS (K200 butir 13): hanya Owner. CERMIN `require_owner` di backend/routers/tanya_etams.py.
export const TANYA_ETAMS_REKAP_ROLES = ["OWNER"];
